import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { getDashboardStats } from '../../api/admin/client';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const data = await getDashboardStats();
      setStats(data.stats);
      setRecentPosts(data.recentPosts || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="admin-loading">
          <div className="admin-spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Dashboard">
        <div className="admin-card">
          <div className="admin-card-body">
            <p style={{ color: 'var(--admin-danger)' }}>Error: {error}</p>
            <button onClick={fetchDashboard} className="admin-btn admin-btn-primary">
              Retry
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      {/* Stats Grid */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-icon blue">
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
          </div>
          <div className="admin-stat-label">Total Posts</div>
          <div className="admin-stat-value">{stats?.posts?.total || 0}</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-icon green">
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="admin-stat-label">Published</div>
          <div className="admin-stat-value">{stats?.posts?.published || 0}</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-icon orange">
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <div className="admin-stat-label">Drafts</div>
          <div className="admin-stat-value">{stats?.posts?.draft || 0}</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-icon purple">
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
          <div className="admin-stat-label">Categories</div>
          <div className="admin-stat-value">{stats?.categories || 0}</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Quick Actions</h3>
        </div>
        <div className="admin-card-body">
          <div className="admin-flex admin-gap-4" style={{ flexWrap: 'wrap' }}>
            <Link to="/admin/posts/new" className="admin-btn admin-btn-primary">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Post
            </Link>
            <Link to="/admin/categories" className="admin-btn admin-btn-secondary">
              Manage Categories
            </Link>
            <Link to="/admin/media" className="admin-btn admin-btn-secondary">
              Media Library
            </Link>
          </div>
        </div>
      </div>

      {/* Recent Posts */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Recent Posts</h3>
          <Link to="/admin/posts" className="admin-btn admin-btn-sm admin-btn-secondary">
            View All
          </Link>
        </div>
        <div className="admin-card-body" style={{ padding: 0 }}>
          {recentPosts.length === 0 ? (
            <div className="admin-empty">
              <p>No posts yet</p>
              <Link to="/admin/posts/new" className="admin-btn admin-btn-primary" style={{ marginTop: '16px' }}>
                Create your first post
              </Link>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentPosts.map(post => (
                  <tr key={post.id}>
                    <td>
                      <Link to={`/admin/posts/${post.id}`} style={{ color: 'var(--admin-primary)', textDecoration: 'none', fontWeight: 500 }}>
                        {post.title.length > 50 ? post.title.substring(0, 50) + '...' : post.title}
                      </Link>
                    </td>
                    <td>{post.categoryName || '-'}</td>
                    <td>
                      <span className={`admin-badge admin-badge-${post.status === 'published' ? 'success' : post.status === 'draft' ? 'warning' : 'info'}`}>
                        {post.status}
                      </span>
                    </td>
                    <td className="admin-text-muted">
                      {new Date(post.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
